import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/States";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const WHO = [{ value: "agent", label: "Agent" }, { value: "engineer", label: "Engineer" }, { value: "locksmith", label: "Locksmith" }];

export const WalkAwayDialog = ({ open, onOpenChange, onConfirm, busy, tid, address }) => {
  const [by, setBy] = useState([]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setBy(address?.status === "walk_away" ? address.walk_away_by || [] : []);
      setReason(address?.status === "walk_away" ? address.walk_away_reason || "" : "");
      setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (v, on) => {
    setError("");
    setBy((p) => (on ? [...new Set([...p, v])] : p.filter((x) => x !== v)));
  };
  const submit = (e) => {
    e.preventDefault();
    if (!by.length) return setError("Select Agent, Engineer, Locksmith or any combination");
    if (!reason.trim()) return setError("Give a reason for the walk away");
    setError("");
    onConfirm({ walk_away_by: by, walk_away_reason: reason.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${tid}-walk-away-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Walk away</DialogTitle>
          <DialogDescription>Who walked away from {address?.full_address || address?.postcode || "this address"}{address?.full_address ? `, ${address.postcode}` : ""}, and why?</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="flex gap-3">
            {WHO.map((w) => (
              <label key={w.value} className="flex min-h-[44px] flex-1 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium">
                <Checkbox checked={by.includes(w.value)} onCheckedChange={(v) => toggle(w.value, !!v)} data-testid={`${tid}-walk-away-${w.value}`} />
                {w.label}
              </label>
            ))}
          </div>
          <div>
            <Label htmlFor={`${tid}-wa-reason`}>Reason *</Label>
            <Textarea id={`${tid}-wa-reason`} rows={4} value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }} maxLength={1000} placeholder="Why did you walk away?" className="mt-1.5" data-testid={`${tid}-walk-away-reason`} />
          </div>
          <FieldError message={error} testId={`${tid}-walk-away-error`} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${tid}-walk-away-cancel`}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid={`${tid}-walk-away-save`}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save walk away
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const WalkAwayNote = ({ address, testId, onEdit }) => {
  if (address.status !== "walk_away" || !address.walk_away_reason) return null;
  const labels = { agent: "Agent", engineer: "Engineer", locksmith: "Locksmith" };
  const who = (address.walk_away_by || []).map((w) => labels[w] || w).join(" & ");
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border-l-4 border-rose-500 bg-rose-50 p-3 text-sm text-rose-900" data-testid={testId}>
      <p className="flex-1 whitespace-pre-line"><span className="font-semibold">Walk away{who ? ` (${who})` : ""}: </span>{address.walk_away_reason}</p>
      {onEdit && <Button size="icon" variant="ghost" onClick={onEdit} aria-label="Edit walk away reason" className="h-7 w-7 text-rose-700" data-testid={`${testId}-edit`}><Pencil className="h-3.5 w-3.5" /></Button>}
    </div>
  );
};
